import * as pdf from "pdfjs-dist";
import "./pdfviewer.css";

// @ts-ignore
import "pdfjs-dist/build/pdf.worker.min.mjs";
import { Observable } from "../observable";

pdf.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

type ViewOptions = {
    pageNumber: number;
    scale: number;
};

const MAX_SCALE = 3;
const MIN_SCALE = 0.25;
const SCALE_STEP = 0.25;

class PDFViewerElement extends HTMLElement {
    static observedAttributes = ["src"];

    #src = new Observable<string | null>(null);
    #unsubs: Function[] = [];
    #pdfState = new Observable<{
        pdfDocument: pdf.PDFDocumentProxy | null;
        options: ViewOptions;
    }>({
        pdfDocument: null,
        options: {
            pageNumber: 1,
            scale: 1,
        },
    });

    constructor() {
        super();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            switch (name) {
                case "src":
                    this.#src.broadcast(newValue);
                    break;
            }
        }
    }

    connectedCallback() {
        this.#setupElements();
        const unsub = this.#src.subscribe((src) => {
            if (src) {
                pdf.getDocument(src).promise.then((doc) => {
                    this.#pdfState.broadcast((prev) => ({
                        ...prev,
                        pdfDocument: doc,
                    }));
                });
            }
        }, true);
        this.#unsubs.push(unsub);
    }

    #setupElements() {
        this.innerHTML = ""; // prevents duplicate rendering when element is not completely destroyed

        // create elements
        const toolbar = document.createElement("div");
        toolbar.className = "bd-pdfviewer-toolbar";

        const pageCounter = document.createElement("span");
        pageCounter.className = "bd-pdfviewer-pagecounter";

        const nextButton = document.createElement("button");
        nextButton.className = "bd-button bd-surface-20 bd-button-icon";
        nextButton.innerHTML = `<bd-icon value="nav-arrow-right"></bd-icon>`;
        nextButton.onclick = () => {
            if (this.#pdfState.data.pdfDocument) {
                this.#pdfState.broadcast((prev) => ({
                    ...prev,
                    options: {
                        ...prev.options,
                        pageNumber: Math.min(
                            this.#pdfState.data.pdfDocument!.numPages,
                            prev.options.pageNumber + 1,
                        ),
                    },
                }));
            }
        };

        const prevButton = document.createElement("button");
        prevButton.className = "bd-button bd-surface-20 bd-button-icon";
        prevButton.innerHTML = `<bd-icon value="nav-arrow-left"></bd-icon>`;
        prevButton.onclick = () => {
            if (this.#pdfState.data.pdfDocument) {
                this.#pdfState.broadcast((prev) => ({
                    ...prev,
                    options: {
                        ...prev.options,
                        pageNumber: Math.max(prev.options.pageNumber - 1, 1),
                    },
                }));
            }
        };

        const zoomInButton = document.createElement("button");
        zoomInButton.className = "bd-button bd-surface-20 bd-button-icon";
        zoomInButton.innerHTML = `<bd-icon value="zoom-in"></bd-icon>`;
        zoomInButton.onclick = () => {
            if (this.#pdfState.data.pdfDocument) {
                this.#pdfState.broadcast((prev) => ({
                    ...prev,
                    options: {
                        ...prev.options,
                        scale: Math.min(
                            prev.options.scale + SCALE_STEP,
                            MAX_SCALE,
                        ),
                    },
                }));
            }
        };

        const zoomOutButton = document.createElement("button");
        zoomOutButton.className = "bd-button bd-surface-20 bd-button-icon";
        zoomOutButton.innerHTML = `<bd-icon value="zoom-out"></bd-icon>`;
        zoomOutButton.onclick = () => {
            if (this.#pdfState.data.pdfDocument) {
                this.#pdfState.broadcast((prev) => ({
                    ...prev,
                    options: {
                        ...prev.options,
                        scale: Math.max(
                            prev.options.scale - SCALE_STEP,
                            MIN_SCALE,
                        ),
                    },
                }));
            }
        };

        const canvasScroller = document.createElement("div");
        canvasScroller.className = "bd-pdfviewer-canvasscroller";

        const canvasWrapper = document.createElement("div");
        canvasWrapper.className = "bd-pdfviewer-canvaswrapper";

        const canvasLoader = document.createElement("div");
        canvasLoader.className = "bd-pdfviewer-canvasloading";

        // register elements
        toolbar.appendChild(prevButton);
        toolbar.appendChild(pageCounter);
        toolbar.appendChild(nextButton);
        toolbar.appendChild(zoomInButton);
        toolbar.appendChild(zoomOutButton);
        this.appendChild(toolbar);
        canvasScroller.appendChild(canvasWrapper);
        this.appendChild(canvasScroller);

        let firstRender = true;

        // reactivity
        this.#unsubs.push(
            this.#pdfState.subscribe((state) => {
                const { pdfDocument, options } = state;
                const { pageNumber, scale } = options;

                nextButton.disabled =
                    !pdfDocument || pageNumber >= pdfDocument.numPages;
                prevButton.disabled = !pdfDocument || pageNumber <= 1;
                pageCounter.textContent = !pdfDocument
                    ? "0 / 0"
                    : `${pageNumber} / ${pdfDocument.numPages}`;

                zoomInButton.disabled = !pdfDocument || scale >= MAX_SCALE;
                zoomOutButton.disabled = !pdfDocument || scale <= MIN_SCALE;

                if (pdfDocument) {
                    this.#renderPDFPage(pdfDocument, options).then((canvas) => {
                        if (firstRender) {
                            canvas.className = "fade-in";
                            firstRender = false;
                        }
                        canvasWrapper.innerHTML = "";
                        canvasWrapper.appendChild(canvas);
                    });
                } else {
                    setTimeout(() => {
                        if (!canvasWrapper.hasChildNodes()) {
                            canvasWrapper.innerHTML = "";
                            canvasWrapper.appendChild(canvasLoader);
                        }
                    }, 1_000);
                }
            }, true),
        );
    }

    async #renderPDFPage(pdfDoc: pdf.PDFDocumentProxy, options: ViewOptions) {
        const canvas = document.createElement("canvas");
        if (!canvas) {
            throw Error("failed to setup canvas");
        }
        const page = await pdfDoc.getPage(options.pageNumber);
        const viewport = page.getViewport({ scale: options.scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const renderTask = page.render({ canvas, viewport });

        return renderTask.promise.then(() => canvas);
    }

    disconnectedCallback() {
        this.#unsubs.forEach((unsub) => {
            unsub();
        });
        this.#unsubs = [];
    }
}

customElements.define("bd-pdfviewer", PDFViewerElement);
